import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/logo.webp";
import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, Menu, X, ChevronDown, ExternalLink } from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import ThemeToggle from "@/components/theme/ThemeToggle";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState<string | null>(null);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const isEmpresasActive = location.pathname.startsWith("/servicos/empresas");

  const menuStructure = {
    empresas: {
      title: "Para Empresas",
      items: [
        { title: "Avaliações / Laudos Psicológicos", href: "/servicos/empresas/laudos-psicologicos" },
        { title: "Avaliação / Atestado Psicosocial", href: "/servicos/empresas/atestado-psicosocial" },
        { title: "Saúde Mental NR-1", href: "/servicos/empresas/saude-mental-nr1" },
        { title: "Terapia para Empresas", href: "/servicos/empresas/terapia-empresas" },
      ],
    },
  };

  const toggleMobileSubmenu = (key: string) => {
    setOpenMobileSubmenu(openMobileSubmenu === key ? null : key);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-card/95 backdrop-blur-md border-b border-border shadow-soft">
      {/* Equilybra Psicoterapias Banner */}
      <div className="gradient-cta py-2">
        <div className="container mx-auto px-4 flex items-center justify-center gap-2 text-sm">
          <span className="text-primary-foreground font-medium">
            🧠 Atendimento para Pessoas Físicas?
          </span>
          <a
            href="https://www.equilybra.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary-foreground font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity"
          >
            Conheça a Equilybra Psicoterapias
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Top bar with contact info */}
      <div className="bg-primary text-primary-foreground py-2">
        <div className="container mx-auto px-4 flex flex-wrap justify-center md:justify-between items-center gap-2 text-sm">
          <div className="flex items-center gap-4">
            <a href="tel:1123050747" className="flex items-center gap-1 hover:opacity-80 transition-opacity">
              <Phone className="w-4 h-4" />
              <span>(11) 2305-0747</span>
            </a>
            <a href="https://wa.me/5511976974905" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:opacity-80 transition-opacity">
              <MessageCircle className="w-4 h-4" />
              <span>(11) 97697-4905 (somente mensagem)</span>
            </a>
          </div>
          <a href="mailto:contato@equilybracompany.com.br" className="hidden md:block text-primary-foreground hover:opacity-80 transition-opacity">
            contato@equilybracompany.com.br
          </a>
          <ThemeToggle variant="onPrimary" className="h-7 w-7 ml-2" />
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={logo}
              alt="Equilybra Company"
              width={126}
              height={80}
              className="h-20 w-auto object-contain transition-[filter] duration-300 dark:[filter:drop-shadow(0_0_10px_hsl(var(--primary)/0.45))_drop-shadow(0_0_22px_hsl(0_0%_100%/0.15))]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link 
              to="/" 
              className={cn(
                "relative px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                isActive("/")
                  ? "bg-accent text-accent-foreground font-semibold after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-primary"
                  : "text-foreground hover:bg-accent/50"
              )}
            >
              Home
            </Link>
            <Link 
              to="/sobre" 
              className={cn(
                "relative px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                isActive("/sobre")
                  ? "bg-accent text-accent-foreground font-semibold after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-primary"
                  : "text-foreground hover:bg-accent/50"
              )}
            >
              Sobre
            </Link>
            <Link
              to="/cases-corporativos"
              className={cn(
                "relative px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                isActive("/cases-corporativos")
                  ? "bg-accent text-accent-foreground font-semibold after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-primary"
                  : "text-foreground hover:bg-accent/50"
              )}
            >
              Cases B2B
            </Link>
            
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={cn(
                      "bg-transparent hover:bg-accent/50 data-[state=open]:bg-accent",
                      isEmpresasActive
                        ? "bg-accent text-accent-foreground font-semibold"
                        : "text-foreground"
                    )}
                  >
                    Serviços Corporativos
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[400px] p-4">
                      <div className="space-y-1">
                        <div className="px-3 py-2">
                          <h4 className="text-sm font-semibold text-foreground flex items-center gap-1">
                            Para Empresas
                          </h4>
                        </div>
                        {menuStructure.empresas.items.map((item) => (
                          <NavigationMenuLink key={item.href} asChild>
                            <Link
                              to={item.href}
                              className={cn(
                                "block select-none rounded-lg px-3 py-2 text-sm leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                                isActive(item.href) &&
                                  "bg-accent text-accent-foreground font-semibold border-l-2 border-primary"
                              )}
                            >
                              {item.title}
                            </Link>
                          </NavigationMenuLink>
                        ))}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link 
              to="/contato" 
              className={cn(
                "relative px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                isActive("/contato")
                  ? "bg-accent text-accent-foreground font-semibold after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-primary"
                  : "text-foreground hover:bg-accent/50"
              )}
            >
              Contato
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Button variant="whatsapp" size="default" asChild>
              <a href="https://wa.me/5511976974905?text=Olá!%20Gostaria%20de%20mais%20informações%20sobre%20a%20Equilybra%20Company." target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </Button>
            <Button variant="call" size="default" asChild>
              <a href="tel:1123050747">
                <Phone className="w-4 h-4" />
                Ligue Agora
              </a>
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center gap-2 lg:hidden">
            <ThemeToggle />
            <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={isMenuOpen}
            className="p-2 rounded-lg hover:bg-accent transition-colors"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border animate-fade-in max-h-[70vh] overflow-y-auto">
            <nav className="flex flex-col gap-1">
              <Link 
                to="/" 
                onClick={() => setIsMenuOpen(false)}
                className={cn(
                  "px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  isActive("/")
                    ? "bg-accent text-accent-foreground font-semibold border-l-4 border-primary"
                    : "text-foreground hover:bg-accent/50"
                )}
              >
                Home
              </Link>
              <Link 
                to="/sobre" 
                onClick={() => setIsMenuOpen(false)}
                className={cn(
                  "px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  isActive("/sobre")
                    ? "bg-accent text-accent-foreground font-semibold border-l-4 border-primary"
                    : "text-foreground hover:bg-accent/50"
                )}
              >
                Sobre
              </Link>
              <Link
                to="/cases-corporativos"
                onClick={() => setIsMenuOpen(false)}
                className={cn(
                  "px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  isActive("/cases-corporativos")
                    ? "bg-accent text-accent-foreground font-semibold border-l-4 border-primary"
                    : "text-foreground hover:bg-accent/50"
                )}
              >
                Cases B2B
              </Link>
              
              <div className="px-4 py-2 mt-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Serviços Corporativos</span>
              </div>

              {/* Para Empresas - Mobile Submenu */}
              <button
                onClick={() => toggleMobileSubmenu('empresas')}
                className={cn(
                  "px-6 py-2 text-sm rounded-lg transition-colors flex items-center justify-between w-full text-left",
                  isEmpresasActive
                    ? "bg-accent text-accent-foreground font-semibold"
                    : "text-foreground hover:bg-accent/50"
                )}
              >
                <span>Para Empresas</span>
                <ChevronDown className={cn("w-4 h-4 transition-transform", openMobileSubmenu === 'empresas' && "rotate-180")} />
              </button>
              {openMobileSubmenu === 'empresas' && (
                <div className="pl-8 space-y-1">
                  {menuStructure.empresas.items.map((item) => (
                    <Link 
                      key={item.href}
                      to={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className={cn(
                        "block px-4 py-2 text-sm rounded-lg transition-colors",
                        isActive(item.href)
                          ? "bg-accent text-accent-foreground font-semibold border-l-4 border-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                      )}
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              )}
              
              <Link 
                to="/contato" 
                onClick={() => setIsMenuOpen(false)}
                className={cn(
                  "px-4 py-3 rounded-lg text-sm font-medium transition-colors mt-2",
                  isActive("/contato")
                    ? "bg-accent text-accent-foreground font-semibold border-l-4 border-primary"
                    : "text-foreground hover:bg-accent/50"
                )}
              >
                Contato
              </Link>

              <div className="flex flex-col gap-2 mt-4 px-4">
                <Button variant="whatsapp" size="lg" className="w-full" asChild>
                  <a href="https://wa.me/5511976974905?text=Olá!%20Gostaria%20de%20mais%20informações%20sobre%20a%20Equilybra%20Company." target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </a>
                </Button>
                <Button variant="call" size="lg" className="w-full" asChild>
                  <a href="tel:1123050747">
                    <Phone className="w-4 h-4" />
                    Ligue Agora
                  </a>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
